import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { toast } from "sonner";
import { 
  ShieldCheck, 
  Search, 
  Building2, 
  Sparkles, 
  Truck, 
  Briefcase, 
  Bell, 
  CheckCircle2, 
  Send, 
  Clock, 
  Filter, 
  Plus, 
  Eye, 
  Check, 
  X,
  Layers,
  MapPin,
  Mail,
  RefreshCw
} from "lucide-react";

export const ConciergeAdminDesk = () => {
  const { user, token } = useAuth();

  const [activeTab, setActiveTab] = useState("matching");
  const [briefs, setBriefs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [matches, setMatches] = useState([]);
  const [moveRequests, setMoveRequests] = useState([]);
  const [partnerSubs, setPartnerSubs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manual Matching Desk State
  const [selectedBriefId, setSelectedBriefId] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [conciergeNote, setConciergeNote] = useState("Hand-picked match fitting your budget and pet requirements in Kensington.");
  const [similarityTag, setSimilarityTag] = useState("Direct Match • Terrace & Pets Approved");
  const [publicNote, setPublicNote] = useState("");
  const [matchingSubmitting, setMatchingSubmitting] = useState(false);

  // Property Filter in Matching Engine
  const [filterBorough, setFilterBorough] = useState("All");

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [bRes, pRes, mRes, movRes, partRes, notifRes] = await Promise.all([
        api.get("/briefs"),
        api.get("/properties"),
        api.get("/matches"),
        api.get("/move-requests"),
        api.get("/partner-submissions"),
        api.get("/notifications")
      ]);
      setBriefs(bRes.data);
      setProperties(pRes.data);
      setMatches(mRes.data);
      setMoveRequests(movRes.data);
      setPartnerSubs(partRes.data);
      setNotifications(notifRes.data);

      if (bRes.data.length > 0 && !selectedBriefId) {
        setSelectedBriefId(bRes.data[0].id);
      }
      if (pRes.data.length > 0 && !selectedPropertyId) {
        setSelectedPropertyId(pRes.data[0].id);
      }
    } catch (err) {
      console.error("Admin desk error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [user, token]);

  // MANUAL MATCH CREATION & DISPATCH
  const handleDispatchMatch = async (e) => {
    e.preventDefault();
    if (!selectedBriefId || !selectedPropertyId) {
      toast.error("Please select both a Tenant Brief and a Property.");
      return;
    }
    setMatchingSubmitting(true);
    try {
      await api.post("/matches", {
        brief_id: selectedBriefId,
        property_id: selectedPropertyId,
        concierge_notes: conciergeNote,
        similarity_tag: similarityTag,
        public_note: publicNote.trim() || null,
      });
      toast.success("Match created and dispatched to Tenant Portal!");
      setPublicNote("");
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create match");
    } finally {
      setMatchingSubmitting(false);
    }
  };

  // Move Lead Assignment
  const handleAssignMovePartner = async (reqId, partnerName) => {
    try {
      await api.patch(`/move-requests/${reqId}`, {
        assigned_partner: partnerName,
        dispatch_status: "Assigned to Partner"
      });
      toast.success(`Move inquiry assigned to ${partnerName}`);
      fetchAllData();
    } catch (e) {
      toast.error("Failed to assign partner");
    }
  };

  // Partner Intake Status Update
  const handleUpdatePartnerStatus = async (subId, newStatus) => {
    try {
      await api.patch(`/partner-submissions/${subId}`, { status: newStatus });
      toast.success(`Partner status updated to: ${newStatus}`);
      fetchAllData();
    } catch (e) {
      toast.error("Could not update partner");
    }
  };

  const selectedBrief = briefs.find((b) => b.id === selectedBriefId);
  const filteredProperties = filterBorough === "All" ? properties : properties.filter((p) => p.borough === filterBorough);

  return (
    <div className="min-h-screen bg-[#E8F2FE] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header Bar */}
        <div className="bg-[#091F5C] text-white rounded-2xl p-6 sm:p-8 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-900/80 border border-[#334DAF] text-[#D0E4FE] text-xs font-semibold">
              <ShieldCheck className="w-4 h-4 text-[#D0E4FE]" />
              <span>FIND LET Concierge Desk • Operations Console</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold font-heading">
              Concierge Operations Desk
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 max-w-xl">
              Manual property-tenant matching, intake queue review, FIND LET Move dispatching, and live transactional email event monitoring.
            </p>
          </div>

          <div className="flex flex-col items-end gap-3">
            <button
              onClick={fetchAllData}
              className="px-3.5 py-2 rounded-xl bg-[#091F5C] hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center space-x-1.5 border border-slate-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span>Refresh Desk</span>
            </button>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="p-3 bg-[#091F5C]/80 rounded-xl border border-slate-700">
                <div className="text-xl font-bold font-heading text-white">{briefs.length}</div>
                <div className="text-[10px] text-slate-400">Tenant Briefs</div>
              </div>
              <div className="p-3 bg-[#091F5C]/80 rounded-xl border border-slate-700">
                <div className="text-xl font-bold font-heading text-[#7096D1]">{properties.length}</div>
                <div className="text-[10px] text-slate-400">Properties</div>
              </div>
              <div className="p-3 bg-[#091F5C]/80 rounded-xl border border-slate-700">
                <div className="text-xl font-bold font-heading text-[#7096D1]">{matches.length}</div>
                <div className="text-[10px] text-slate-400">Matches Dispatched</div>
              </div>
              <div className="p-3 bg-[#091F5C]/80 rounded-xl border border-slate-700">
                <div className="text-xl font-bold font-heading text-[#7096D1]">{moveRequests.length}</div>
                <div className="text-[10px] text-slate-400">Move Inquiries</div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-[#E5EBF5] pb-2">
          {[
            { id: "matching", label: "Manual Match Engine", icon: Sparkles },
            { id: "briefs", label: `Tenant Briefs (${briefs.length})`, icon: Search },
            { id: "properties", label: `Landlord Listings (${properties.length})`, icon: Building2 },
            { id: "moves", label: `Move Dispatch (${moveRequests.length})`, icon: Truck },
            { id: "partners", label: `Partner Ingestion (${partnerSubs.length})`, icon: Briefcase },
            { id: "notifications", label: `Transactional Email Log (${notifications.length})`, icon: Mail }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 ${
                  isActive 
                    ? "bg-[#091F5C] text-white shadow-md" 
                    : "bg-white text-[#4A5B78] border border-[#E5EBF5] hover:bg-[#F9FBFF]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* ================= TAB 1: MANUAL MATCH ENGINE ================= */}
        {activeTab === "matching" && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E5EBF5] shadow-sm space-y-4">
              <div>
                <h2 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-[#334DAF]" />
                  <span>Manual Property-Tenant Match Engine</span>
                </h2>
                <p className="text-xs text-[#4A5B78] mt-0.5">
                  Select a tenant brief, pick a compatible property from active inventory, add your personalized Concierge Note, and dispatch directly to the tenant's portal.
                </p>
              </div>

              <form onSubmit={handleDispatchMatch} className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-2">
                
                {/* Left: Tenant Brief Selection */}
                <div className="lg:col-span-4 space-y-3">
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider">
                    1. Select Tenant Brief
                  </label>
                  <select
                    value={selectedBriefId}
                    onChange={(e) => setSelectedBriefId(e.target.value)}
                    data-testid="admin-select-brief"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#E5EBF5] text-xs font-medium"
                  >
                    {briefs.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.full_name} • £{b.budget_pcm_min} - £{b.budget_pcm_max} ({b.target_boroughs?.join(", ")})
                      </option>
                    ))}
                  </select>

                  {/* Selected Brief Details Card */}
                  {selectedBrief && (
                    <div className="p-4 rounded-xl bg-[#F9FBFF] border border-[#E5EBF5] text-xs space-y-2 text-[#4A5B78]">
                      <div className="font-bold text-[#091F5C] text-sm">{selectedBrief.full_name}</div>
                      <div className="text-[#334DAF] font-semibold">{selectedBrief.email} • {selectedBrief.phone}</div>
                      <div>
                        <strong>Boroughs:</strong> {selectedBrief.target_boroughs?.join(", ")}
                      </div>
                      <div>
                        <strong>Budget:</strong> £{selectedBrief.budget_pcm_min?.toLocaleString()} - £{selectedBrief.budget_pcm_max?.toLocaleString()} PCM
                      </div>
                      <div>
                        <strong>Move Date:</strong> {selectedBrief.move_in_date} ({selectedBrief.household_composition})
                      </div>
                      <div>
                        <strong>Criteria:</strong> {selectedBrief.pets ? "Pets Approved • " : ""}{selectedBrief.outside_space ? "Balcony/Garden • " : ""}{selectedBrief.furnishing_preference}
                      </div>
                      {selectedBrief.additional_notes && (
                        <div className="text-[11px] text-[#4A5B78] italic bg-white p-2 rounded border">
                          "{selectedBrief.additional_notes}"
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Center: Property Selection */}
                <div className="lg:col-span-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider">
                      2. Select Property from Inventory
                    </label>
                    <select
                      value={filterBorough}
                      onChange={(e) => setFilterBorough(e.target.value)}
                      data-testid="admin-filter-borough-select"
                      className="text-[11px] px-2 py-1 rounded border border-[#E5EBF5]"
                    >
                      <option value="All">All Boroughs</option>
                      <option value="Kensington and Chelsea">Kensington</option>
                      <option value="Islington">Islington</option>
                      <option value="Canary Wharf">Canary Wharf</option>
                      <option value="Camden">Camden</option>
                      <option value="Clapham">Clapham</option>
                      <option value="Marylebone">Marylebone</option>
                    </select>
                  </div>

                  <div className="max-h-72 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                    {filteredProperties.map((p) => {
                      const isSelected = selectedPropertyId === p.id;
                      return (
                        <div
                          key={p.id}
                          onClick={() => setSelectedPropertyId(p.id)}
                          data-testid={`admin-prop-option-${p.id}`}
                          className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                            isSelected 
                              ? "bg-[#E8F2FE] border-[#D0E4FE] text-blue-950 font-semibold shadow-sm"
                              : "bg-white border-[#E5EBF5] hover:border-[#D0E4FE] text-[#4A5B78]"
                          }`}
                        >
                          <div className="space-y-0.5">
                            <div className="text-xs font-bold text-[#091F5C]">{p.title}</div>
                            <div className="text-[11px] text-[#4A5B78]">
                              {p.borough} ({p.postcode}) • {p.bedrooms} Beds • £{p.rent_pcm?.toLocaleString()} PCM
                            </div>
                          </div>
                          {isSelected && <Check className="w-4 h-4 text-[#334DAF] flex-shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right: Concierge Recommendation Note & Dispatch */}
                <div className="lg:col-span-3 space-y-3 flex flex-col justify-between">
                  <div className="space-y-3">
                    <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider">
                      3. Concierge Pairing Note
                    </label>
                    
                    <textarea
                      rows={3}
                      required
                      placeholder="Why does this property suit the tenant's brief?"
                      value={conciergeNote}
                      onChange={(e) => setConciergeNote(e.target.value)}
                      data-testid="admin-concierge-note-input"
                      className="w-full px-3 py-2 rounded-xl border border-[#E5EBF5] text-xs focus:ring-2 focus:ring-[#334DAF]"
                    />

                    <div>
                      <label className="block text-[11px] font-bold text-[#4A5B78] uppercase mb-1">
                        Similarity Tag
                      </label>
                      <input
                        type="text"
                        value={similarityTag}
                        onChange={(e) => setSimilarityTag(e.target.value)}
                        data-testid="admin-similarity-tag-input"
                        className="w-full px-3 py-1.5 rounded-xl border border-[#E5EBF5] text-xs"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-[#4A5B78] uppercase mb-1">
                        Tenant-Facing "Why we picked this" <span className="text-[#7096D1] font-medium normal-case">(optional, shown on match card)</span>
                      </label>
                      <textarea
                        rows={2}
                        maxLength={220}
                        placeholder="A short line the tenant will read on their match card — e.g. 'Rare Georgian conversion two minutes from Highbury Fields, matches your quiet street preference.'"
                        value={publicNote}
                        onChange={(e) => setPublicNote(e.target.value)}
                        data-testid="admin-public-note-input"
                        className="w-full px-3 py-2 rounded-xl border border-[#E5EBF5] text-xs focus:ring-2 focus:ring-[#334DAF]"
                      />
                      <div className="text-[10px] text-[#7096D1] mt-1 text-right">{publicNote.length}/220</div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={matchingSubmitting}
                    data-testid="admin-dispatch-match-btn"
                    className="w-full py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-bold text-xs shadow-md transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>{matchingSubmitting ? "Dispatching..." : "Dispatch Match to Tenant"}</span>
                  </button>
                </div>

              </form>
            </div>

            {/* Active Matches Table */}
            <div className="bg-white rounded-2xl border border-[#E5EBF5] p-6 shadow-sm space-y-4">
              <h3 className="text-base font-bold text-[#091F5C] font-heading">
                All Dispatched Concierge Matches ({matches.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs text-[#4A5B78]">
                  <thead className="bg-[#F9FBFF] text-[#4A5B78] uppercase text-[10px]">
                    <tr>
                      <th className="p-3">Tenant</th>
                      <th className="p-3">Property Matched</th>
                      <th className="p-3">Rent PCM</th>
                      <th className="p-3">Concierge Note</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {matches.map((m) => (
                      <tr key={m.id} className="hover:bg-[#F9FBFF]">
                        <td className="p-3 font-semibold text-[#091F5C]">
                          {m.tenant_name || "Eleanor Vane"} <br />
                          <span className="text-[10px] text-slate-400">{m.tenant_email}</span>
                        </td>
                        <td className="p-3 font-medium">
                          {m.property_title} <br />
                          <span className="text-[10px] text-slate-400">{m.property_borough}</span>
                        </td>
                        <td className="p-3 font-mono font-bold">£{m.property_rent_pcm?.toLocaleString()}</td>
                        <td className="p-3 max-w-xs truncate italic">"{m.concierge_notes}"</td>
                        <td className="p-3">
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#D0E4FE] text-blue-800">
                            {m.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ================= TAB 2: TENANT BRIEFS ================= */}
        {activeTab === "briefs" && (
          <div className="bg-white rounded-2xl border border-[#E5EBF5] p-6 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading">
              Tenant Intake Briefs Queue ({briefs.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-[#4A5B78]">
                <thead className="bg-[#F9FBFF] text-[#4A5B78] uppercase text-[10px]">
                  <tr>
                    <th className="p-3">Renter</th>
                    <th className="p-3">Target Boroughs</th>
                    <th className="p-3">Budget</th>
                    <th className="p-3">Move Date</th>
                    <th className="p-3">Lifestyle Non-Negotiables</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {briefs.map((b) => (
                    <tr key={b.id} className="hover:bg-[#F9FBFF]">
                      <td className="p-3 font-semibold text-[#091F5C]">
                        {b.full_name} <br />
                        <span className="text-[10px] text-slate-400">{b.email}</span>
                      </td>
                      <td className="p-3 text-[#334DAF] font-medium">{b.target_boroughs?.join(", ")}</td>
                      <td className="p-3 font-mono font-bold">£{b.budget_pcm_min?.toLocaleString()} - £{b.budget_pcm_max?.toLocaleString()}</td>
                      <td className="p-3">{b.move_in_date} ({b.household_composition})</td>
                      <td className="p-3">
                        {b.pets ? "Pets Approved • " : ""}{b.outside_space ? "Balcony/Garden • " : ""}{b.furnishing_preference}
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#D0E4FE] text-emerald-800">
                          {b.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= TAB 3: LANDLORD PROPERTIES ================= */}
        {activeTab === "properties" && (
          <div className="bg-white rounded-2xl border border-[#E5EBF5] p-6 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading">
              Active Property Inventory ({properties.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-[#4A5B78]">
                <thead className="bg-[#F9FBFF] text-[#4A5B78] uppercase text-[10px]">
                  <tr>
                    <th className="p-3">Property</th>
                    <th className="p-3">Borough / Postcode</th>
                    <th className="p-3">Rent PCM</th>
                    <th className="p-3">Beds / Baths</th>
                    <th className="p-3">Verification</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {properties.map((p) => (
                    <tr key={p.id} className="hover:bg-[#F9FBFF]">
                      <td className="p-3 font-semibold text-[#091F5C]">{p.title}</td>
                      <td className="p-3">{p.borough} ({p.postcode})</td>
                      <td className="p-3 font-mono font-bold">£{p.rent_pcm?.toLocaleString()}</td>
                      <td className="p-3">{p.bedrooms} Beds • {p.bathrooms} Baths</td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#D0E4FE] text-emerald-800">
                          {p.verification_status || "Verified"}
                        </span>
                      </td>
                      <td className="p-3 font-medium text-[#091F5C]">{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= TAB 4: MOVE DISPATCH ================= */}
        {activeTab === "moves" && (
          <div className="bg-white rounded-2xl border border-[#E5EBF5] p-6 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading">
              FIND LET Move Dispatch Desk ({moveRequests.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-[#4A5B78]">
                <thead className="bg-[#F9FBFF] text-[#4A5B78] uppercase text-[10px]">
                  <tr>
                    <th className="p-3">Client</th>
                    <th className="p-3">Route</th>
                    <th className="p-3">Services</th>
                    <th className="p-3">Target Date</th>
                    <th className="p-3">Assigned Partner</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {moveRequests.map((req) => (
                    <tr key={req.id} className="hover:bg-[#F9FBFF]">
                      <td className="p-3 font-semibold text-[#091F5C]">
                        {req.full_name} <br />
                        <span className="text-[10px] text-slate-400">{req.email}</span>
                      </td>
                      <td className="p-3 font-mono">{req.pickup_postcode} &rarr; {req.delivery_postcode}</td>
                      <td className="p-3">{req.services?.join(", ")}</td>
                      <td className="p-3">{req.move_date}</td>
                      <td className="p-3 font-bold text-[#334DAF]">
                        {req.assigned_partner || "Unassigned"}
                      </td>
                      <td className="p-3">
                        {!req.assigned_partner && (
                          <button
                            type="button"
                            onClick={() => handleAssignMovePartner(req.id, "Apex London White-Glove Removals")}
                            data-testid={`assign-partner-btn-${req.id}`}
                            className="px-2.5 py-1 rounded bg-[#091F5C] hover:bg-[#061745] text-white text-[10px] font-bold"
                          >
                            Assign Apex London
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= TAB 5: PARTNER INGESTION ================= */}
        {activeTab === "partners" && (
          <div className="bg-white rounded-2xl border border-[#E5EBF5] p-6 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-[#091F5C] font-heading">
              Property Partner Applications & Feed Ingestions ({partnerSubs.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-[#4A5B78]">
                <thead className="bg-[#F9FBFF] text-[#4A5B78] uppercase text-[10px]">
                  <tr>
                    <th className="p-3">Agency</th>
                    <th className="p-3">Contact</th>
                    <th className="p-3">Coverage</th>
                    <th className="p-3">Integration</th>
                    <th className="p-3">Feed / Links</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {partnerSubs.map((sub) => (
                    <tr key={sub.id} className="hover:bg-[#F9FBFF]">
                      <td className="p-3 font-bold text-[#091F5C]">{sub.agency_name}</td>
                      <td className="p-3">
                        {sub.contact_name} <br />
                        <span className="text-[10px] text-slate-400">{sub.contact_email}</span>
                      </td>
                      <td className="p-3">{sub.borough_coverage?.join(", ")}</td>
                      <td className="p-3">{sub.integration_type}</td>
                      <td className="p-3 font-mono text-[10px] max-w-xs truncate">{sub.feed_or_links}</td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#D0E4FE] text-purple-800">
                          {sub.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= TAB 6: TRANSACTIONAL EMAIL LOG ================= */}
        {activeTab === "notifications" && (
          <div className="bg-white rounded-2xl border border-[#E5EBF5] p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-[#091F5C] font-heading">
                  Transactional Notifications & Email Activity
                </h2>
                <p className="text-xs text-[#4A5B78]">
                  Live record of brief confirmations, concierge match dispatches, and viewing notifications sent to tenants & landlords.
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded bg-[#E8F2FE] text-blue-800 border border-[#D0E4FE]">
                Resend / In-App Event Center
              </span>
            </div>

            <div className="divide-y divide-slate-100">
              {notifications.map((n) => (
                <div key={n.id} className="py-4 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-[#091F5C]">{n.subject}</span>
                    <span className="text-[10px] text-slate-400">{n.created_at}</span>
                  </div>
                  <div className="text-xs text-[#4A5B78]">
                    To: <strong className="text-[#091F5C]">{n.recipient_name}</strong> &lt;{n.recipient_email}&gt;
                  </div>
                  <div className="text-[11px] text-[#334DAF] font-medium flex items-center space-x-1">
                    <CheckCircle2 className="w-3 h-3" />
                    <span>Status: {n.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
